import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarX, CircleNotch, X } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";
import { toISO, fromISO } from "@/lib/dates";
import { PICKUP_WINDOWS } from "@/lib/booking";

const short = (w) => w.split(" (")[0];
const pretty = (iso) => fromISO(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

function SlotCloser({ windows, setWindows, fullDays }) {
  const [day, setDay] = useState("");
  const toggle = (w) => setWindows((all) => {
    const cur = all[day] || [];
    const next = cur.includes(w) ? cur.filter((x) => x !== w) : [...cur, w];
    const { [day]: _, ...rest } = all;
    return next.length ? { ...rest, [day]: next } : rest;
  });
  const entries = Object.entries(windows).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="mt-6 pt-5 border-t border-[#F0F0F1]" data-testid="admin-slot-closer">
      <h4 className="font-display font-extrabold text-[#15306E]">Close a time slot</h4>
      <p className="text-sm text-[#52525B] mt-1">Pick a day, then tap the slots that are full. Closing all 4 slots blocks the whole day.</p>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <input type="date" data-testid="admin-slot-date" min={toISO(new Date())} value={day} onChange={(e) => setDay(e.target.value)}
          className="border border-[#E4E4E7] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E]" />
        {day && (fullDays.includes(day) ? <span className="text-sm text-[#C0392B] font-bold">Whole day is blocked above.</span> : PICKUP_WINDOWS.map((w, i) => {
          const on = (windows[day] || []).includes(w);
          return (
            <button key={w} type="button" data-testid={`admin-slot-toggle-${i}`} onClick={() => toggle(w)}
              className={`text-xs font-bold px-3 py-2 rounded-sm border-2 transition-colors ${on ? "border-[#C0392B] bg-[#FBEAEA] text-[#C0392B] line-through" : "border-[#E4E4E7] text-[#15306E] hover:border-[#A1A1AA]"}`}>
              {short(w)}
            </button>
          );
        }))}
      </div>
      {day && (windows[day] || []).length === PICKUP_WINDOWS.length && (
        <p className="text-xs text-[#C0392B] mt-2" data-testid="admin-slot-fullday-hint">All 4 slots closed: this day will become a fully booked day when you save.</p>
      )}
      {entries.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2" data-testid="admin-closed-slots">
          {entries.map(([d, ws]) => ws.map((w) => (
            <li key={d + w} className="flex items-center gap-1.5 bg-[#FBEAEA] text-[#C0392B] text-xs font-bold pl-2.5 pr-1 py-1 rounded-full">
              {pretty(d)} · {short(w)}
              <button type="button" aria-label="Reopen slot" onClick={() => { setDay(d); setWindows((all) => { const n = (all[d] || []).filter((x) => x !== w); const { [d]: _, ...rest } = all; return n.length ? { ...rest, [d]: n } : rest; }); }}
                className="p-0.5 rounded-full hover:bg-white/70"><X size={11} weight="bold" /></button>
            </li>
          )))}
        </ul>
      )}
    </div>
  );
}

export default function BlockedDaysCard() {
  const [days, setDays] = useState(null);
  const [windows, setWindows] = useState({});
  const [saving, setSaving] = useState(false);

  const apply = (data) => { setDays(data.dates.map(fromISO)); setWindows(data.windows || {}); };
  useEffect(() => {
    api.get("/blocked-dates").then(({ data }) => apply(data)).catch(() => setDays([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/blocked-dates", { dates: days.map(toISO), windows });
      apply(data);
      toast.success("Blocked days and time slots saved.");
    } catch { toast.error("Couldn't save blocked days."); }
    finally { setSaving(false); }
  };

  const slotCount = Object.values(windows).reduce((s, w) => s + w.length, 0);
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5 mb-6" data-testid="admin-blocked-days">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-sm">
          <h3 className="font-display font-extrabold text-[#15306E] flex items-center gap-2"><CalendarX size={20} weight="duotone" className="text-[#C0392B]" /> Fully booked days</h3>
          <p className="text-sm text-[#52525B] mt-1">Tap days to block or unblock them. Customers can't pick blocked days on the booking page.</p>
          <p className="text-sm font-bold text-[#15306E] mt-3" data-testid="admin-blocked-count">{days ? `${days.length} day${days.length === 1 ? "" : "s"} blocked · ${slotCount} slot${slotCount === 1 ? "" : "s"} closed` : ""}</p>
          <button type="button" data-testid="admin-blocked-save" onClick={save} disabled={!days || saving}
            className="mt-3 bg-[#15306E] hover:bg-[#0F2350] text-white text-sm font-bold px-5 py-2.5 rounded-sm disabled:opacity-60 flex items-center gap-2">
            {saving && <CircleNotch size={15} className="animate-spin" />} Save blocked days &amp; slots
          </button>
        </div>
        {days === null ? <CircleNotch size={28} className="animate-spin text-[#46A01E]" /> : (
          <Calendar mode="multiple" selected={days} onSelect={(d) => setDays(d || [])} disabled={{ before: new Date() }} numberOfMonths={2}
            modifiersClassNames={{ selected: "!bg-[#C0392B] !text-white line-through" }} data-testid="admin-blocked-calendar" />
        )}
      </div>
      {days && <SlotCloser windows={windows} setWindows={setWindows} fullDays={days.map(toISO)} />}
    </div>
  );
}
